import React, { useState, useEffect } from 'react';
import { api } from '../api';

function CreateCampaignModal({ onClose, onSuccess }) {
  const [audioFiles, setAudioFiles] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    caller_id: '',
    audio_file_id: '',
    transfer_numbers: [''],
    max_concurrency: 1
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadAudio();
  }, []);

  const loadAudio = async () => {
    try {
      const data = await api.getAudioFiles();
      setAudioFiles(data);
      if (data.length > 0) {
        setFormData(prev => ({ ...prev, audio_file_id: data[0].id }));
      }
    } catch (error) {
      setError('Failed to load audio files');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    // Filter out empty transfer numbers
    const cleanedData = {
      ...formData,
      transfer_numbers: formData.transfer_numbers.filter(n => n.trim())
    };

    if (cleanedData.transfer_numbers.length === 0) {
      setError('At least one transfer number is required');
      setSubmitting(false);
      return;
    }

    try {
      await api.createCampaign(cleanedData);
      onSuccess();
    } catch (error) {
      setError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const addTransferNumber = () => {
    setFormData(prev => ({
      ...prev,
      transfer_numbers: [...prev.transfer_numbers, '']
    }));
  };

  const removeTransferNumber = (index) => {
    setFormData(prev => ({
      ...prev,
      transfer_numbers: prev.transfer_numbers.filter((_, i) => i !== index)
    }));
  };

  const updateTransferNumber = (index, value) => {
    setFormData(prev => ({
      ...prev,
      transfer_numbers: prev.transfer_numbers.map((n, i) => i === index ? value : n)
    }));
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal">
          <div className="loading"><div className="spinner"></div></div>
        </div>
      </div>
    );
  }

  if (audioFiles.length === 0) {
    return (
      <div className="modal-overlay">
        <div className="modal">
          <div className="modal-header">
            <h3 className="modal-title">Create Campaign</h3>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
          <div className="error-message">
            Please upload at least one audio file before creating a campaign.
          </div>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3 className="modal-title">Create New Campaign</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Campaign Name *</label>
            <input
              type="text"
              className="form-input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              data-testid="input-campaign-name"
            />
          </div>


          <div className="form-group">
            <label className="form-label">Audio File *</label>
            <select
              className="form-select"
              value={formData.audio_file_id}
              onChange={(e) => setFormData({ ...formData, audio_file_id: e.target.value })}
              required
              data-testid="select-audio"
            >
              {audioFiles.map(audio => (
                <option key={audio.id} value={audio.id}>
                  {audio.original_filename}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Transfer Destination *</label>
            <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.5rem' }}>
              Enter a phone number (e.g., 12223334444) or SIP URI (e.g., sip:agent@worldoftelco.me)
            </p>
            {formData.transfer_numbers.map((number, index) => (
              <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <input
                  type="text"
                  className="form-input"
                  placeholder="12223334444 or sip:agent@worldoftelco.me"
                  value={number}
                  onChange={(e) => updateTransferNumber(index, e.target.value)}
                  data-testid={`input-transfer-${index}`}
                />
                {formData.transfer_numbers.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => removeTransferNumber(index)}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="btn btn-secondary"
              style={{ marginTop: '0.5rem' }}
              onClick={addTransferNumber}
              data-testid="btn-add-transfer"
            >
              + Add Transfer Destination
            </button>
          </div>

          <div className="form-group">
            <label className="form-label">Max Concurrent Calls</label>
            <select
              className="form-select"
              value={formData.max_concurrency}
              onChange={(e) => setFormData({ ...formData, max_concurrency: parseInt(e.target.value) })}
              data-testid="select-max-concurrency"
            >
              <option value="1">1</option>
              {!(JSON.parse(localStorage.getItem('user') || '{}').restrict_concurrency_ui) &&
               (JSON.parse(localStorage.getItem('user') || '{}').max_concurrency_allowed || 1) >= 2 && (
                <option value="2">2</option>
              )}
              {!(JSON.parse(localStorage.getItem('user') || '{}').restrict_concurrency_ui) &&
               (JSON.parse(localStorage.getItem('user') || '{}').max_concurrency_allowed || 1) >= 3 && (
                <option value="3">3</option>
              )}
            </select>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
              data-testid="btn-submit-campaign"
            >
              {submitting ? 'Creating...' : 'Create Campaign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateCampaignModal;
